/**
 * The shape of the user guide.
 *
 * <p>Content as data rather than prose in JSX, for one reason that matters: option tables are
 * built from the same label maps the dropdowns use, so a value added to the application appears
 * in the guide automatically. A guide written as free text drifts the first time somebody adds a
 * status, and a guide that is wrong is worse than none — people stop trusting the parts that are
 * still right.
 */

/** One row of a "what does this option mean" table. */
export interface GuideOption {
  name: string;
  meaning: string;
}

export interface GuideTable {
  title: string;
  intro?: string;
  options: GuideOption[];
}

/** A worked example, because a list of fields does not teach anybody the job. */
export interface GuideExample {
  title: string;
  steps: string[];
}

export interface GuideSection {
  id: string;
  title: string;
  /** Where it lives, so somebody can go and look at it while reading. */
  where: string;
  purpose: string;
  examples: GuideExample[];
  tables: GuideTable[];
  /** Rules the application enforces that surprise people the first time. */
  notes?: string[];
}

export interface GuideChapter {
  id: string;
  title: string;
  sections: GuideSection[];
}

/**
 * Builds an option table from the labels the application actually uses.
 *
 * <p>The meanings are written here; the list of values is not. Both records are keyed by the same
 * union, so adding a status to the application stops this file compiling until somebody explains
 * what it means — which is the only reliable way a guide keeps up with the thing it describes.
 */
export function optionsFrom<T extends string>(
  labels: Record<T, string>,
  meanings: Record<T, string>,
  order?: T[],
): GuideOption[] {
  const keys = order ?? (Object.keys(labels) as T[]);
  return keys.map((key) => ({ name: labels[key], meaning: meanings[key] }));
}
